import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store';
import { loadSuggestions } from '../redux/suggestions';
import { AnalysisMessageData, MessageReceived, MessageReceivedEvent } from '../models/webSockets';
import { selectSuggestions } from '../selectors/suggestions';
import { useEffect, useRef } from 'react';

export const useSuggestionStatusHandler = () => {
  const dispatch = useDispatch<AppDispatch>();
  const suggestions = useSelector(selectSuggestions);
  const suggestionsRef = useRef<typeof suggestions>(suggestions);
  const foundChangesRef = useRef<boolean>(false);
  const pollingRef = useRef<{
    attempts: number;
    originalStatuses: string[];
    intervalId: ReturnType<typeof setInterval> | null;
  } | null>(null);

  // Keep suggestionsRef up to date
  useEffect(() => {
    suggestionsRef.current = suggestions;
  }, [suggestions]);

  // Helper to compare statuses
  const haveStatusesChanged = (original: string[], current: string[]) => {
    if ((original.length !== current.length) || original.some((status, idx) => status !== current[idx])) {
      console.log('original:', original);
      console.log('current:', current);
      console.log('suggestions:', suggestions);
      foundChangesRef.current = true;
      return true;
    }
    return false;
  };

  // Polling function
  const startPollingForUpdatedStatuses = (originalStatuses: string[]) => {
    if (pollingRef.current && pollingRef.current.intervalId) {
      clearInterval(pollingRef.current.intervalId);
    }
    let attempts = 0;
    const MAX_ATTEMPTS = 20;
    const ATTEMPT_INTERVAL = 500;
    foundChangesRef.current = false;
    const intervalId = setInterval(() => {
      console.log('attempts', attempts);
      attempts++;
      const currentStatuses = (suggestionsRef.current || []).map(s => s.status);

      if (haveStatusesChanged(originalStatuses, currentStatuses)) {
        console.log(`Polling found changes on attempt #${attempts} of ${MAX_ATTEMPTS}. Next attempt in ${(ATTEMPT_INTERVAL / 1000).toFixed(1)} seconds`);
        originalStatuses = currentStatuses;
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(intervalId);
        pollingRef.current = null;
        console.log('Polling stopped after', attempts, 'attempts. Statuses changed:', foundChangesRef.current);
        return;
      }

      dispatch(loadSuggestions());

      if (pollingRef.current) pollingRef.current.attempts = attempts;
    }, ATTEMPT_INTERVAL);
    pollingRef.current = {
      attempts: 0,
      originalStatuses,
      intervalId,
    };
  };

  const suggestionStatusMessageHandler = async (message: MessageReceived<AnalysisMessageData>) => {
    try {
      if (message.event === MessageReceivedEvent.RECOMMENDATION_STATUS_CHANGE) {
        console.log('Websocket Listener: Recommendation status change event', message.event);
        const originalStatuses = (suggestionsRef.current || []).map(s => s.status);
        startPollingForUpdatedStatuses(originalStatuses);
      }
    } catch (e) {
      console.error('Failed to parse WebSocket message:', e);
    }
  };

  return {
    suggestionStatusMessageHandler,
  };
};
